import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  FiPlusCircle, FiRefreshCw, FiKey, FiTrash2, FiPower,
  FiCopy, FiAlertTriangle, FiRadio, FiCheckCircle, FiXCircle, FiEdit3, FiCalendar
} from 'react-icons/fi';
import {
  getSources, createSource, updateSource, rotateSourceKey, deleteSource, maskSourceKey
} from '../../services/signalSourceService';
import { fetchApplications } from '../../services/licenseService';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { fmtTime } from '../../utils/format';

const SourceManager = () => {
  const queryClient = useQueryClient();

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', applicationId: '', targetGroups: '', pushExpiresAt: '' });
  const [plainKey, setPlainKey] = useState(null); // 新建/轮换后一次性展示的明文
  const [deleteId, setDeleteId] = useState(null);
  const [rotateId, setRotateId] = useState(null);
  // 编辑态: 改名/改组/改推送到期日
  const [editSource, setEditSource] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', targetGroups: '', pushExpiresAt: '' });

  const { data, isLoading, error, refetch } = useQuery('signalSources', getSources, {
    refetchInterval: 30000
  });
  const sources = data?.sources || [];

  const { data: applications } = useQuery('applications', fetchApplications);

  const createMutation = useMutation(createSource, {
    onSuccess: (data) => {
      toast.success(data.message || '策略源已创建');
      setPlainKey(data.sourceKeyPlain || null);
      setShowCreate(false);
      setForm({ name: '', applicationId: '', targetGroups: '', pushExpiresAt: '' });
      queryClient.invalidateQueries('signalSources');
    },
    onError: (err) => toast.error(err.message || '创建失败')
  });

  const updateMutation = useMutation(
    ({ id, data }) => updateSource(id, data),
    {
      onSuccess: () => {
        toast.success('策略源已更新');
        queryClient.invalidateQueries('signalSources');
      },
      onError: (err) => toast.error(err.message || '更新失败')
    }
  );

  const rotateMutation = useMutation(rotateSourceKey, {
    onSuccess: (data) => {
      toast.success(data.message || '凭证已轮换');
      setPlainKey(data.sourceKeyPlain || null);
      setRotateId(null);
      queryClient.invalidateQueries('signalSources');
    },
    onError: (err) => toast.error(err.message || '轮换失败')
  });

  const deleteMutation = useMutation(deleteSource, {
    onSuccess: () => {
      toast.success('策略源已删除');
      setDeleteId(null);
      queryClient.invalidateQueries('signalSources');
    },
    onError: (err) => toast.error(err.message || '删除失败')
  });

  const handleCreate = () => {
    if (!form.name.trim()) { toast.error('请填写策略源名称'); return; }
    if (!form.applicationId) { toast.error('请选择归属应用'); return; }
    const groups = form.targetGroups
      .split(/[,，]/).map(g => g.trim()).filter(Boolean);
    createMutation.mutate({
      name: form.name.trim(),
      applicationId: parseInt(form.applicationId, 10),
      targetGroups: groups,
      pushExpiresAt: form.pushExpiresAt || null
    });
  };

  const openEdit = (s) => {
    setEditSource(s);
    setEditForm({
      name: s.name || '',
      targetGroups: parseGroups(s.targetGroups).join(', '),
      pushExpiresAt: s.pushExpiresAt ? new Date(s.pushExpiresAt).toISOString().split('T')[0] : ''
    });
  };

  const handleEdit = () => {
    if (!editForm.name.trim()) { toast.error('请填写策略源名称'); return; }
    const groups = editForm.targetGroups
      .split(/[,，]/).map(g => g.trim()).filter(Boolean);
    updateMutation.mutate({
      id: editSource.id,
      data: {
        name: editForm.name.trim(),
        targetGroups: groups,
        pushExpiresAt: editForm.pushExpiresAt || null
      }
    }, { onSuccess: () => setEditSource(null) });
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key).then(
      () => toast.success('已复制到剪贴板'),
      () => toast.error('复制失败, 请手动复制')
    );
  };

  const parseGroups = (json) => {
    if (!json) return [];
    try {
      const arr = JSON.parse(json);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  };

  return (
    <div className="space-y-6">
      {/* 页头 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1f2328]">策略源管理</h1>
          <p className="text-[#656d76] text-sm mt-1">管理云端信号推送器凭证：每个策略源一把独立钥匙，可定向分组、随时停用或轮换</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="secondary" icon={<FiRefreshCw />} className="px-3" />
          <Button onClick={() => setShowCreate(true)} variant="primary" icon={<FiPlusCircle />}>
            新建策略源
          </Button>
        </div>
      </div>

      {/* 明文凭证一次性展示 */}
      {plainKey && (
        <div className="bg-[#dafbe1] border border-[#4ac26b] rounded-md p-4 space-y-2">
          <div className="flex items-center gap-2 text-[#1a7f37] font-medium text-sm">
            <FiCheckCircle /> 推送凭证已生成，请立即保存
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-[#d0d7de] rounded-md px-3 py-2 text-sm font-mono text-[#1f2328] break-all">{plainKey}</code>
            <Button onClick={() => copyKey(plainKey)} variant="secondary" icon={<FiCopy />} className="shrink-0">
              复制
            </Button>
          </div>
          <p className="text-xs text-[#1a7f37]">此明文仅显示这一次，刷新后无法再次查看；泄露时用"轮换"换钥匙即可。</p>
          <button onClick={() => setPlainKey(null)} className="text-xs text-[#57606a] hover:underline">我知道了, 收起</button>
        </div>
      )}

      {/* 说明 */}
      <div className="flex items-start gap-2 bg-[#ddf4ff] border border-[#b6e3ff] rounded-md p-3 text-xs text-[#0c447c]">
        <FiRadio className="shrink-0 mt-0.5" />
        <span>
          推送器 EA 使用 <code className="font-mono">sourceKey + appId</code> 调用 POST /api/signals/push 上报开仓/平仓信号。
          目标分组留空 = 发给所有开了跟单开关的授权码；填了分组 = 只发对应分组的授权码。
        </span>
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-md border border-[#d0d7de] shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            <div className="skeleton h-12" /><div className="skeleton h-12" /><div className="skeleton h-12" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-red-500 mb-4">获取策略源列表失败</div>
            <Button onClick={() => refetch()} variant="primary">重试</Button>
          </div>
        ) : sources.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[#656d76] px-6">
            <div className="w-16 h-16 rounded-md brand-soft brand-text flex items-center justify-center mb-5">
              <FiRadio className="text-3xl" />
            </div>
            <p className="text-xl text-[#24292f] font-medium">还没有策略源</p>
            <p className="mt-2 text-center">先创建应用并生成授权码，再建策略源；策略源的推送凭证供策略端 EA 上报信号使用</p>
            <div className="mt-6">
              <Button onClick={() => setShowCreate(true)} variant="primary" icon={<FiPlusCircle />}>
                新建策略源
              </Button>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="data-table min-w-[860px]">
              <thead>
                <tr>
                  <th className="py-3 px-4 text-left">名称</th>
                  <th className="py-3 px-4 text-left">归属应用</th>
                  <th className="py-3 px-4 text-left">推送凭证</th>
                  <th className="py-3 px-4 text-left">目标分组</th>
                  <th className="py-3 px-4 text-left">推送到期</th>
                  <th className="py-3 px-4 text-left">状态</th>
                  <th className="py-3 px-4 text-left">最近推送</th>
                  <th className="py-3 px-4 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(s => {
                  const groups = parseGroups(s.targetGroups);
                  const pushExpired = s.pushExpiresAt && new Date(s.pushExpiresAt).getTime() <= Date.now();
                  return (
                    <tr key={s.id}>
                      <td className="py-3 px-4">
                        <span className="text-[#1f2328] font-medium">{s.name}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs rounded-full brand-soft brand-text inline-flex items-center gap-1">
                          <FiKey className="mr-1" />{s.application?.name || '—'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-xs font-mono text-[#57606a]">{maskSourceKey(s.sourceKey)}</code>
                      </td>
                      <td className="py-3 px-4">
                        {groups.length === 0 ? (
                          <span className="text-xs text-[#57606a]">全部(不限分组)</span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {groups.map(g => (
                              <span key={g} className="text-[10px] px-1.5 py-0.5 rounded bg-[#ddf4ff] text-[#0969da] border border-[#b6e3ff]">{g}</span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs">
                        {s.pushExpiresAt ? (
                          <span className={`inline-flex items-center gap-1 ${pushExpired ? 'text-[#cf222e]' : 'text-[#57606a]'}`}>
                            <FiCalendar size={12} />
                            {fmtTime(s.pushExpiresAt)}
                            {pushExpired && <span className="px-1 rounded bg-[#ffebe9] text-[#cf222e]">已到期</span>}
                          </span>
                        ) : (
                          <span className="text-[#8b949e]">永久</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {s.status === 'active' ? (
                          pushExpired ? (
                            <span className="badge badge-revoked"><FiXCircle className="mr-1" />推送已到期</span>
                          ) : (
                            <span className="badge badge-active badge-active-dot"><FiCheckCircle className="mr-1" />推送中</span>
                          )
                        ) : (
                          <span className="badge badge-revoked"><FiXCircle className="mr-1" />已停用</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-[#57606a] text-xs">
                        {s.lastPushAt ? fmtTime(s.lastPushAt) : '从未推送'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEdit(s)}
                            className="p-1.5 rounded-md transition-colors brand-text hover:opacity-80 hover:bg-[#f6f8fa]"
                            title="编辑(名称/分组/推送到期日)"
                          >
                            <FiEdit3 size={18} />
                          </button>
                          <button
                            onClick={() => updateMutation.mutate({
                              id: s.id,
                              data: { status: s.status === 'active' ? 'disabled' : 'active' }
                            })}
                            className={`p-1.5 rounded-md transition-colors ${s.status === 'active'
                              ? 'text-[#9a6700] hover:bg-[#fff8c5]'
                              : 'text-[#1a7f37] hover:bg-[#dafbe1]'}`}
                            title={s.status === 'active' ? '停用推送' : '恢复推送'}
                          >
                            <FiPower size={18} />
                          </button>
                          <button
                            onClick={() => setRotateId(s.id)}
                            className="p-1.5 brand-text hover:opacity-80 hover:bg-[#f6f8fa] rounded-md transition-colors"
                            title="轮换凭证(旧钥匙立即作废)"
                          >
                            <FiRefreshCw size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteId(s.id)}
                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-[#ffebe9] rounded-md transition-colors"
                            title="删除策略源"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 新建弹窗 */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="新建策略源">
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">策略源名称</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="如: 黄金趋势策略-实盘"
              maxLength={100}
              className="w-full border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">归属应用</label>
            <select
              value={form.applicationId}
              onChange={(e) => setForm({ ...form, applicationId: e.target.value })}
              className="w-full border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            >
              <option value="">请选择应用(客户授权码须在同一应用下)</option>
              {(applications || []).map(app => (
                <option key={app.id} value={app.id}>{app.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">目标分组</label>
            <input
              value={form.targetGroups}
              onChange={(e) => setForm({ ...form, targetGroups: e.target.value })}
              placeholder="留空 = 全部授权码; 多个分组用逗号分隔, 如: VIP, 批发"
              className="w-full border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            />
            <p className="text-xs text-[#8b949e] mt-1">只把信号发给授权码列表里"跟单分组"匹配的客户</p>
          </div>
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">推送到期日</label>
            <input
              type="date"
              value={form.pushExpiresAt}
              onChange={(e) => setForm({ ...form, pushExpiresAt: e.target.value })}
              className="w-full md:w-64 border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            />
            <p className="text-xs text-[#8b949e] mt-1">留空 = 永久; 到期后该策略源的推送会被服务端直接拒收(与授权码到期日各管各)</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setShowCreate(false)} variant="secondary">取消</Button>
            <Button onClick={handleCreate} variant="primary" disabled={createMutation.isLoading}>
              {createMutation.isLoading ? '创建中...' : '创建并生成凭证'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 编辑弹窗: 名称/分组/推送到期日 */}
      <Modal isOpen={!!editSource} onClose={() => setEditSource(null)} title={`编辑策略源: ${editSource?.name || ''}`}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">策略源名称</label>
            <input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              maxLength={100}
              className="w-full border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">目标分组</label>
            <input
              value={editForm.targetGroups}
              onChange={(e) => setEditForm({ ...editForm, targetGroups: e.target.value })}
              placeholder="留空 = 全部授权码; 多个分组用逗号分隔"
              className="w-full border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            />
          </div>
          <div>
            <label className="block text-sm text-[#1f2328] mb-1.5">推送到期日</label>
            <input
              type="date"
              value={editForm.pushExpiresAt}
              onChange={(e) => setEditForm({ ...editForm, pushExpiresAt: e.target.value })}
              className="w-full md:w-64 border border-[#d0d7de] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0969da]"
            />
            <p className="text-xs text-[#8b949e] mt-1">清空 = 永久; 到期后推送被拒收。延期后即时恢复</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setEditSource(null)} variant="secondary">取消</Button>
            <Button onClick={handleEdit} variant="primary" disabled={updateMutation.isLoading}>
              {updateMutation.isLoading ? '保存中...' : '保存'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 轮换确认 */}
      <Modal isOpen={!!rotateId} onClose={() => setRotateId(null)} title="轮换推送凭证">
        <div className="space-y-4">
          <div className="flex items-start gap-2 bg-[#fff8c5] border border-[#f0d875] rounded-md p-3 text-xs text-[#7d4e00]">
            <FiAlertTriangle className="shrink-0 mt-0.5" />
            <span>轮换后旧凭证立即作废，策略端推送器 EA 必须改用新凭证，否则信号上报全部失败。仅当凭证疑似泄露时使用。</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setRotateId(null)} variant="secondary">取消</Button>
            <Button
              onClick={() => rotateMutation.mutate(rotateId)}
              variant="danger"
              disabled={rotateMutation.isLoading}
            >
              {rotateMutation.isLoading ? '轮换中...' : '确认轮换'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 删除确认 */}
      <Modal isOpen={!!deleteId} onClose={() => setDeleteId(null)} title="删除策略源">
        <div className="space-y-4">
          <p className="text-sm text-[#57606a]">确定删除该策略源？删除后凭证立即失效。</p>
          <p className="text-xs text-[#8b949e]">已有信号记录的策略源不能删除，只能停用。</p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setDeleteId(null)} variant="secondary">取消</Button>
            <Button onClick={() => deleteMutation.mutate(deleteId)} variant="danger" disabled={deleteMutation.isLoading}>
              删除
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SourceManager;
